import { AxiosError } from 'axios';
import ReactGA from 'react-ga';
import { Location } from '../api/models/locations';
import { v4 as uuidv4 } from 'uuid';
import { getErrorFromResponse } from './apiHelpers';
import { CreateDeliveryResponse } from '../api/models/deliveries';

enum Categories {
  connection = 'cnx_connection',
  delivery = 'cnx_delivery',
  queuedDelivery = 'cnx_queuedDelivery',
  otp = 'cnx_otp',
  createDelivery = 'cnx_create_delivery',
  faqAnswer = 'cnx_faq_answer',
  dispute = 'dispute',
  walletInvoice = 'wallet_invoice'
}

enum Actions {
  listen = 'listen',
  impression = 'impression',
  click = 'click',
  offline = 'offline',
  created = 'created',
  attemptToCreate = 'attemptToCreate',
  attemptToRecreate = 'attemptToRecreate',
  recreated = 'recreated',
  failed = 'failed',
  like = 'like',
  dislike = 'dislike',
  fetched = 'fetched'
}

enum Dimensions {
  valueData = 'dimension1',
  sessionId = 'dimension2',
  timestamp = 'dimension3',
  customerId = 'dimension4'
}

/**
 * This function does two things
 * 1. Defere the passed function `fun` to the next tick in event-loop to not block the main app logic
 * 2. Protect main app from func `fun` if it throws an error, yet to protect the main app logic
 * @param fun any function
 */
const guardFunctionCall = (fun: (...args: any[]) => void) => {
  setTimeout(() => {
    try {
      fun();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, 0);
};

const setValueData = (data: any) => {
  ReactGA.set({ [Dimensions.valueData]: JSON.stringify(data) });
};

type ReportEventProps = {
  category: Categories;
  action: Actions;
  label?: string;
  value?: number;
};

const reportEventNoGuard = ({ category, action, label, value }: ReportEventProps) => {
  ReactGA.set({ [Dimensions.timestamp]: new Date().toISOString() });
  ReactGA.event({
    category,
    action,
    value,
    label
  });
};

export const reportEvent = (props: ReportEventProps) =>
  guardFunctionCall(() => {
    reportEventNoGuard(props);
  });

export const reportError = (error: string, fatal = false) =>
  guardFunctionCall(() => {
    ReactGA.set({ [Dimensions.timestamp]: new Date().toISOString() });
    ReactGA.exception({
      description: error,
      fatal
    });
  });

export const reportOffline = () =>
  guardFunctionCall(() => {
    performance.mark('offline');
  });

export const reportOnline = () =>
  guardFunctionCall(() => {
    performance.measure('offline-time', 'offline');

    const measures = performance.getEntriesByName('offline-time', 'measure');

    performance.clearMarks('offline');
    performance.clearMeasures('offline-time');

    if (measures.length > 0) {
      const measure = measures[measures.length - 1];

      reportEvent({ category: Categories.connection, action: Actions.offline, value: measure.duration });
    }
  });

export const reportApiCallError = (axiosError: AxiosError) => {
  guardFunctionCall(() => {
    if (axiosError.response) {
      const response = axiosError.response;
      const error = {
        url: response.request?.responseURL,
        status: response.status,
        statusText: response.statusText,
        message: response.data?.message,
        method: axiosError.config?.method
      };
      reportError(JSON.stringify(error));
    }
  });
};

const reportCreateDeliveryResponse = (value: any) => {
  guardFunctionCall(() => {
    setValueData(value);
    reportEventNoGuard({
      category: Categories.createDelivery,
      action: Actions.listen,
      label: 'create_delivery_response'
    });
  });
};

export const reportDeliveryCreated = (deliveryResponse: CreateDeliveryResponse = {}, queued?: boolean) => {
  const category = queued ? Categories.queuedDelivery : Categories.delivery;
  reportEvent({ category: category, action: Actions.created });
  reportCreateDeliveryResponse({
    response: 'success',
    message: 'successfully order placed',
    delivery_id: deliveryResponse.id,
    booking_id: deliveryResponse.bookingId
  });
};

export const reportAttemptToCreateDelivery = () => {
  reportEvent({ category: Categories.delivery, action: Actions.attemptToCreate });
};

export const reportAttemptToRecreateDelivery = () => {
  reportEvent({ category: Categories.delivery, action: Actions.attemptToRecreate });
};

export const reportDeliveryRecreated = (bookingId: string, deliveryResponse: CreateDeliveryResponse = {}) => {
  reportEvent({ category: Categories.delivery, action: Actions.recreated, label: bookingId });
  reportCreateDeliveryResponse({
    response: 'success',
    message: 'successfully order placed',
    delivery_id: deliveryResponse.id,
    booking_id: deliveryResponse.bookingId
  });
};

export const reportDeliveryCreationFailed = (error: AxiosError, queued?: boolean) => {
  guardFunctionCall(() => {
    const category = queued ? Categories.queuedDelivery : Categories.delivery;
    const errorMessage = getErrorFromResponse(error);
    reportEvent({ category: category, action: Actions.failed, label: errorMessage });
    reportCreateDeliveryResponse({
      response: 'failure',
      message: `error due to ${errorMessage}`
    });
  });
};

export const reportOtpCreated = () => {
  reportEvent({ category: Categories.otp, action: Actions.created });
};

export const reportOtpCreationFailed = (responseMessage: string) => {
  reportEvent({ category: Categories.otp, action: Actions.failed, label: responseMessage });
};

export const reportDeliveryButtonWasClicked = (deliveryData: {
  cashOnDelivery?: 'string';
  customerName?: 'string';
  customerPhoneNumber?: 'string';
  deliveryDetails?: 'string';
  dropOffLocation?: Location;
  orderId?: 'string';
  paymentMethodId?: 'string';
  referenceCode?: 'string';
  pickupLocation?: Location;
}) => {
  guardFunctionCall(() => {
    const value = {
      pickup_lat: deliveryData.pickupLocation?.location?.lat,
      pickup_lng: deliveryData.pickupLocation?.location?.lng,
      dropoff_lat: deliveryData.dropOffLocation?.location?.lat,
      dropoff_lng: deliveryData.dropOffLocation?.location?.lng,
      dropoff_cust_name: deliveryData.customerName,
      dropoff_cust_phone: deliveryData.customerPhoneNumber,
      dropoff_delivery_details: deliveryData.deliveryDetails,
      dropoff_cash_collect: deliveryData.cashOnDelivery,
      order_id_merchant: deliveryData.orderId,
      payment_id: deliveryData.paymentMethodId,
      invoice_reference_code: deliveryData.referenceCode
    };
    setValueData(value);
    reportEventNoGuard({ category: Categories.createDelivery, action: Actions.click, label: 'create_delivery' });
  });
};

export const setUserId = (userId: number) => {
  guardFunctionCall(() => {
    ReactGA.set({ userId: userId, [Dimensions.customerId]: userId });
  });
};

export const reportPageVisited = (url: string) => {
  guardFunctionCall(() => {
    ReactGA.pageview(url);
  });
};

export const reportSessionLaunch = () => {
  guardFunctionCall(() => {
    ReactGA.set({ [Dimensions.sessionId]: uuidv4() });
    reportEvent({ category: Categories.createDelivery, action: Actions.impression, label: 'session_launch' });
  });
};

export const reportLikeAnswer = (question: string) => {
  reportEvent({ category: Categories.faqAnswer, action: Actions.like, label: question });
};

export const reportDislikeAnswer = (question: string) => {
  reportEvent({ category: Categories.faqAnswer, action: Actions.dislike, label: question });
};

export const init = (key: string) => {
  ReactGA.initialize(key);
};

export const reportAttemptToCreateDispute = () => {
  reportEvent({ category: Categories.dispute, action: Actions.attemptToCreate });
};

export const reportDisputeCreated = () => {
  reportEvent({ category: Categories.dispute, action: Actions.created });
};

export const reportDisputeCreationFailed = (responseMessage: string) => {
  reportEvent({ category: Categories.dispute, action: Actions.failed, label: responseMessage });
};

export const reportAttemptToCreateInvoice = () => {
  reportEvent({ category: Categories.walletInvoice, action: Actions.attemptToCreate });
};

export const reportInvoiceCreated = () => {
  reportEvent({ category: Categories.walletInvoice, action: Actions.created });
};

export const reportInvoiceCreationFailed = (responseMessage: string) => {
  reportEvent({ category: Categories.walletInvoice, action: Actions.failed, label: responseMessage });
};

export const reportInvoiceFetched = () => {
  reportEvent({ category: Categories.walletInvoice, action: Actions.fetched });
};

export const reportInvoiceFetchFailed = (responseMessage: string) => {
  reportEvent({ category: Categories.walletInvoice, action: Actions.failed, label: responseMessage });
};
